import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { usePomodoro, fmtTime } from '../hooks/usePomodoro';
import MiniChart from '../components/common/MiniChart';

// Fullscreen office-TV view.
//  LEFT half  = Pomodoro zone. Running: dial on top + the next 2-3 tasks below.
//               Stopped: a compact timer bar + the full upcoming task list.
//  RIGHT half = report tiles, rotating through one report at a time.

const WINDOW_DAYS = 14; // how far ahead to pull scheduled days when the timer is stopped
const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };
const label12 = (t) => {
  const [h, m] = t.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
};
const dayLabel = (dateKey) => {
  const t0 = toKey(new Date());
  const t1 = toKey(addDays(new Date(), 1));
  if (dateKey === t0) return 'Today';
  if (dateKey === t1) return 'Tomorrow';
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};
const trendColor = (t) => (t === 'up' ? 'text-emerald-400' : t === 'down' ? 'text-red-400' : 'text-gray-500');

const PomodoroTV = () => {
  const { token } = useContext(AuthContext);
  const socket = useSocket();
  const { connected, isFocus, running, secondsLeft, pct, completed, focusTask, send } = usePomodoro();
  const [agenda, setAgenda] = useState([]); // scheduled items across the next WINDOW_DAYS days
  const [reports, setReports] = useState([]);
  const [now, setNow] = useState(new Date());
  const [rot, setRot] = useState(0); // rotation tick for cycling reports

  // Pull the next WINDOW_DAYS days so the stopped view can show the full upcoming schedule.
  const loadSchedule = useCallback(async () => {
    try {
      const days = Array.from({ length: WINDOW_DAYS }, (_, i) => addDays(new Date(), i));
      const results = await Promise.all(days.map((d) =>
        axios.get(`/api/dayplan/${toKey(d)}`, { headers: { 'x-auth-token': token } })
          .then((r) => ({ d, slots: r.data.slots || [] }))
          .catch(() => ({ d, slots: [] }))
      ));
      const items = [];
      results.forEach(({ d, slots }) => {
        slots.filter((s) => s.type && s.type !== 'free').forEach((s) => {
          const [h, m] = s.time.split(':').map(Number);
          items.push({ ...s, dateKey: toKey(d), when: new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m) });
        });
      });
      items.sort((a, b) => a.when - b.when);
      setAgenda(items);
    } catch (e) { /* keep prior on transient error */ }
  }, [token]);

  const loadReports = useCallback(async () => {
    try {
      const res = await axios.get('/api/reports/tv', { headers: { 'x-auth-token': token } });
      setReports(res.data || []);
    } catch (e) { /* keep prior on transient error */ }
  }, [token]);

  useEffect(() => {
    loadSchedule(); loadReports();
    const poll = setInterval(() => { loadSchedule(); loadReports(); }, 60000);
    const clock = setInterval(() => setNow(new Date()), 15000);
    const rotate = setInterval(() => setRot((r) => r + 1), 30000);
    return () => { clearInterval(poll); clearInterval(clock); clearInterval(rotate); };
  }, [loadSchedule, loadReports]);

  useEffect(() => {
    if (!socket) return undefined;
    const onReport = () => loadReports();
    socket.on('report:updated', onReport);
    return () => { socket.off('report:updated', onReport); };
  }, [socket, loadReports]);

  const nowTs = now.getTime();
  const upcoming = agenda.filter((s) => s.when.getTime() + 30 * 60000 > nowTs);

  const statReports = reports.filter((r) => (r.result?.stats || []).length > 0);
  const activeReport = statReports.length ? statReports[rot % statReports.length] : null;

  const ringColor = isFocus ? '#3b82f6' : '#10b981';
  const R = 52;

  const goFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  };

  // ---- task list ----
  const TaskRow = ({ s }) => {
    const ts = s.when.getTime();
    const isCurrent = ts <= nowTs && nowTs < ts + 30 * 60000;
    return (
      <div className={`flex items-center gap-[2vmin] rounded-2xl px-[2vmin] py-[1.4vmin] border ${isCurrent ? 'bg-blue-600/20 border-blue-500' : 'bg-gray-900/60 border-gray-800'}`}>
        <span className={`text-[2.2vmin] font-black tabular-nums w-[13vmin] flex-shrink-0 ${isCurrent ? 'text-blue-300' : 'text-gray-500'}`}>{label12(s.time)}</span>
        <div className="min-w-0 flex-1">
          <p className={`text-[2.4vmin] font-bold truncate ${s.worked ? 'line-through text-gray-600' : 'text-gray-100'}`}>
            {s.type === 'task' ? (s.card?.title || 'Task') : (s.label || 'Appointment')}
          </p>
        </div>
        {s.type === 'appointment' && <span className="text-[1.3vmin] font-black uppercase tracking-widest text-emerald-400 flex-shrink-0">Appt</span>}
      </div>
    );
  };

  // Flat list (used while running, next few items).
  const TaskList = ({ limit }) => {
    const items = limit ? upcoming.slice(0, limit) : upcoming;
    return (
      <div className="space-y-[1.2vmin]">
        {items.length === 0 && <p className="text-[2.2vmin] text-gray-600 italic">Nothing scheduled coming up.</p>}
        {items.map((s, i) => <TaskRow key={i} s={s} />)}
      </div>
    );
  };

  // Multi-day agenda grouped by date with day headers (used while stopped).
  const Agenda = () => {
    if (upcoming.length === 0) return <p className="text-[2.2vmin] text-gray-600 italic">Nothing scheduled coming up.</p>;
    const groups = [];
    upcoming.forEach((s) => {
      const g = groups[groups.length - 1];
      if (g && g.dateKey === s.dateKey) g.items.push(s);
      else groups.push({ dateKey: s.dateKey, items: [s] });
    });
    return (
      <div className="space-y-[2vmin]">
        {groups.map((g) => (
          <div key={g.dateKey}>
            <p className="text-[1.6vmin] font-black uppercase tracking-[0.3em] text-blue-400/80 mb-[1vmin]">{dayLabel(g.dateKey)}</p>
            <div className="space-y-[1.2vmin]">
              {g.items.map((s, i) => <TaskRow key={i} s={s} />)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ctrlBtn = 'rounded-xl font-black uppercase tracking-widest disabled:opacity-40 transition-all';

  // ---- left half ----
  const leftSide = running ? (
    <div className="h-full flex flex-col min-h-0">
      {/* Dial */}
      <div className="flex flex-col items-center flex-shrink-0">
        <span className={`text-[2vmin] font-black uppercase tracking-[0.3em] mb-[0.8vmin] ${isFocus ? 'text-blue-400' : 'text-emerald-400'}`}>
          {isFocus ? 'Focus' : 'Break'}
        </span>
        <div className="relative">
          <svg className="w-[34vmin] h-[34vmin] -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r={R} fill="none" strokeWidth="7" stroke="#1f2937" />
            <circle cx="60" cy="60" r={R} fill="none" strokeWidth="7" strokeLinecap="round" stroke={ringColor}
              strokeDasharray={2 * Math.PI * R} strokeDashoffset={(2 * Math.PI * R) * (1 - pct / 100)}
              style={{ transition: 'stroke-dashoffset 1s linear' }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[9vmin] font-black tabular-nums leading-none">{fmtTime(secondsLeft)}</span>
            <span className="text-[1.4vmin] font-bold uppercase tracking-[0.3em] text-gray-500 mt-[0.6vmin]">{completed} done</span>
          </div>
        </div>
        {focusTask && <p className="mt-[1.2vmin] text-[2.4vmin] font-bold text-gray-200 text-center max-w-[42vmin] line-clamp-2">{focusTask}</p>}
        <div className="flex items-center gap-[1.5vmin] mt-[1.5vmin]">
          <button onClick={() => send('pause')} disabled={!connected} className={`${ctrlBtn} px-[3vmin] py-[1vmin] text-[1.6vmin] bg-amber-500 hover:bg-amber-600`}>Pause</button>
          <button onClick={() => send('skip')} disabled={!connected} className={`${ctrlBtn} px-[2.4vmin] py-[1vmin] text-[1.6vmin] bg-gray-800 hover:bg-gray-700`}>Skip</button>
          <button onClick={() => send('reset')} disabled={!connected} className={`${ctrlBtn} px-[2.4vmin] py-[1vmin] text-[1.6vmin] bg-gray-800 hover:bg-gray-700`}>Reset</button>
        </div>
      </div>
      {/* Next 2-3 tasks below */}
      <div className="flex-1 min-h-0 overflow-hidden mt-[2.5vmin]">
        <h2 className="text-[2vmin] font-black uppercase tracking-[0.3em] text-gray-500 mb-[1.2vmin]">Up Next</h2>
        <TaskList limit={3} />
      </div>
    </div>
  ) : (
    <div className="h-full flex flex-col min-h-0">
      {/* Compact timer bar */}
      <div className="flex items-center justify-between gap-[2vmin] rounded-2xl bg-gray-900/60 border border-gray-800 px-[2.5vmin] py-[1.6vmin] flex-shrink-0">
        <div className="flex items-baseline gap-[1.5vmin]">
          <span className="text-[5vmin] font-black tabular-nums leading-none">{fmtTime(secondsLeft)}</span>
          <span className={`text-[1.4vmin] font-black uppercase tracking-[0.3em] ${isFocus ? 'text-blue-400' : 'text-emerald-400'}`}>{isFocus ? 'Focus' : 'Break'}</span>
          <span className="text-[1.4vmin] font-bold uppercase tracking-widest text-gray-600">{completed} done</span>
        </div>
        <div className="flex items-center gap-[1.2vmin]">
          <button onClick={() => send('start')} disabled={!connected} className={`${ctrlBtn} px-[3vmin] py-[1vmin] text-[1.6vmin] bg-blue-600 hover:bg-blue-500`}>Start</button>
          <button onClick={() => send('reset')} disabled={!connected} className={`${ctrlBtn} px-[2vmin] py-[1vmin] text-[1.6vmin] bg-gray-800 hover:bg-gray-700`}>Reset</button>
        </div>
      </div>
      {/* Full upcoming schedule across days */}
      <div className="flex-1 min-h-0 overflow-y-auto mt-[2.5vmin] pr-[0.5vmin]">
        <h2 className="text-[2vmin] font-black uppercase tracking-[0.3em] text-gray-500 mb-[1.2vmin]">Upcoming Schedule</h2>
        <Agenda />
      </div>
    </div>
  );

  // ---- right half: rotating single report ----
  const rightSide = (
    <div className="h-full flex flex-col min-h-0">
      {!activeReport ? (
        <div className="h-full flex items-center justify-center">
          <p className="text-[2.2vmin] text-gray-700 italic text-center">No reports flagged for TV.<br />Toggle “Show on TV” on a report.</p>
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between mb-[1vmin] flex-shrink-0">
            <h2 className="text-[2vmin] font-black uppercase tracking-[0.3em] text-gray-400 truncate">{activeReport.name}</h2>
            {activeReport.result?.period && <span className="text-[1.3vmin] font-bold text-gray-600 flex-shrink-0 ml-[1vmin]">{activeReport.result.period}</span>}
          </div>
          {activeReport.result?.headline && (
            <p className="text-[1.7vmin] font-bold text-gray-300 mb-[1.5vmin] line-clamp-2 flex-shrink-0">{activeReport.result.headline}</p>
          )}
          {(() => {
            const hasChart = (activeReport.result?.charts || []).length > 0;
            const tiles = activeReport.result?.stats || [];
            return (
              <div className="flex-1 min-h-0 flex flex-col gap-[1.5vmin] overflow-hidden">
                {/* tiles: full grid when no chart, compact 4 when a chart shares the space */}
                <div className={`grid grid-cols-2 gap-[1.5vmin] ${hasChart ? 'flex-shrink-0' : 'flex-1 content-start'} min-h-0`}>
                  {tiles.slice(0, hasChart ? 4 : 6).map((s, i) => (
                    <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-2xl px-[1.8vmin] py-[1.2vmin]">
                      <div className={`${hasChart ? 'text-[3vmin]' : 'text-[3.6vmin]'} font-black tabular-nums text-gray-100 leading-none truncate`}>{s.value}</div>
                      <div className="flex items-center gap-[0.8vmin] mt-[0.6vmin]">
                        <span className="text-[1.3vmin] font-bold uppercase tracking-widest text-gray-500 truncate">{s.label}</span>
                        {s.delta && <span className={`text-[1.3vmin] font-black ${trendColor(s.trend)}`}>{s.delta}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {hasChart && (
                  <div className="flex-1 min-h-0 bg-gray-900/40 border border-gray-800 rounded-2xl p-[1.5vmin]">
                    <MiniChart chart={activeReport.result.charts[0]} color="#3b82f6"
                      labelClass="text-[1.3vmin]" valueClass="text-[1.6vmin]" />
                  </div>
                )}
              </div>
            );
          })()}
          {activeReport.result?.note && <p className="text-[1.3vmin] text-gray-600 mt-[1.2vmin] line-clamp-2 flex-shrink-0">{activeReport.result.note}</p>}
          {statReports.length > 1 && (
            <div className="flex items-center justify-center gap-[1vmin] mt-[1.5vmin] flex-shrink-0">
              {statReports.map((_, i) => (
                <span key={i} className={`h-[1vmin] w-[1vmin] rounded-full ${i === rot % statReports.length ? 'bg-blue-500' : 'bg-gray-700'}`} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="h-screen w-full bg-gray-950 text-white flex flex-col p-[3vmin] overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-[2vmin] flex-shrink-0">
        <div className="flex items-baseline gap-[2vmin]">
          <span className="text-[5vmin] font-black tabular-nums leading-none">
            {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
          <span className="text-[2vmin] font-bold uppercase tracking-widest text-gray-500">
            {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </span>
        </div>
        <div className="flex items-center gap-[2vmin]">
          {!connected && <span className="text-[1.6vmin] font-bold uppercase tracking-widest text-amber-400">Connecting…</span>}
          <button onClick={goFullscreen} title="Toggle fullscreen" className="text-[1.6vmin] font-bold uppercase tracking-widest text-gray-500 hover:text-white transition-colors">⛶ Fullscreen</button>
          <Link to="/daily-plan" className="text-[1.6vmin] font-bold uppercase tracking-widest text-gray-500 hover:text-white transition-colors">Exit</Link>
        </div>
      </div>

      {/* Two halves */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-[4vmin] min-h-0">
        <div className="h-full min-h-0">{leftSide}</div>
        <div className="h-full min-h-0 lg:border-l lg:border-gray-800 lg:pl-[4vmin]">{rightSide}</div>
      </div>
    </div>
  );
};

export default PomodoroTV;
